import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Scale } from 'lucide-react';
import type {
  CompanyStatutoryLeaveTemplateInput, CompanyStatutoryLeaveTemplateView, LeaveTypeView,
} from '../../../models/attendance';
import * as LeavesApi from '../../../api/Attendance/leaves';
import { getCompany } from '../../../api/General/Company';
import { PageShell } from '../../../components/leaves/PageShell';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Skeleton } from '../../../components/ui/Skeleton';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { CopyRow } from './CopyRow';

/**
 * Hr's Statutory Leave Templates (spec statutory-templates-per-company.md, section 8.3). The rows are the
 * company's OWN copies of the platform catalogue (owner Q1 a, 2026-10-15): Hr edits them freely, also below the
 * platform value, which stays visible beside a changed value with a Reset (Q3 a); links each to a leave type;
 * archives and restores. There is no Add (Q5 a). The route and every endpoint are Hr only (Q10 a).
 */

type View = 'active' | 'archived';
const ALL = 'all';

interface CompanyCountry { id: number; name: string; iso: string }
interface HeldCountry { id: number; name: string; iso: string }

const StatutoryLeaveTemplates: React.FC = () => {
  const { t } = useTranslation(['leaves', 'common']);
  const company = useSelector((state: { user: { company: { id: number } | null } }) => state.user.company, shallowEqual);
  const companyId = company?.id;
  const { toast, showToast, dismissToast } = useToast();

  const [copies, setCopies] = useState<CompanyStatutoryLeaveTemplateView[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeView[]>([]);
  const [companyCountry, setCompanyCountry] = useState<CompanyCountry | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<View>('active');
  const [countryFilter, setCountryFilter] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [copiesRes, typesRes, companyRes] = await Promise.all([
        LeavesApi.getAllStatutoryTemplates(true),
        LeavesApi.getCompanyLeaveTypes().catch(() => null),
        companyId ? getCompany(companyId).catch(() => null) : Promise.resolve(null),
      ]);
      if (copiesRes.errors.length > 0) {
        setLoadError(getApiErrorMessage(copiesRes, t, t('leaves:statutoryTemplates.loadError')));
        return;
      }
      setCopies(copiesRes.payload ?? []);
      if (typesRes && typesRes.errors.length === 0) setLeaveTypes(typesRes.payload ?? []);
      const country = companyRes && companyRes.errors.length === 0 ? companyRes.payload?.country : null;
      setCompanyCountry(country ? { id: country.id, name: country.nicename || country.name, iso: country.iso } : null);
    } catch (err) {
      setLoadError(getApiErrorMessage(err, t, t('leaves:statutoryTemplates.loadError')));
    } finally {
      setLoading(false);
    }
  }, [companyId, t]);

  useEffect(() => { load(); }, [load]);

  // Countries the company holds copies for (archived included): the filter shows only when there is more than one
  // (owner Q6 a, the old country's copies stay after a country change).
  const heldCountries = useMemo<HeldCountry[]>(() => {
    const byId = new Map<number, HeldCountry>();
    for (const c of copies) {
      if (!byId.has(c.countryId)) byId.set(c.countryId, { id: c.countryId, name: c.countryName || c.countryIso || String(c.countryId), iso: c.countryIso || '' });
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [copies]);
  const showCountryFilter = heldCountries.length > 1;

  // Default to the company's current country when it holds rows for it.
  useEffect(() => {
    if (countryFilter != null || loading) return;
    const own = companyCountry && heldCountries.some(c => c.id === companyCountry.id) ? String(companyCountry.id) : ALL;
    setCountryFilter(own);
  }, [countryFilter, loading, companyCountry, heldCountries]);

  const effectiveCountry = showCountryFilter ? (countryFilter ?? ALL) : ALL;
  const inCountry = (c: CompanyStatutoryLeaveTemplateView) => effectiveCountry === ALL || String(c.countryId) === effectiveCountry;
  const activeCount = copies.filter(c => !c.isArchived && inCountry(c)).length;
  const archivedCount = copies.filter(c => c.isArchived && inCountry(c)).length;
  const visible = copies.filter(c => (view === 'archived' ? c.isArchived : !c.isArchived) && inCountry(c));

  const groups = useMemo(() => {
    const map = new Map<number, CompanyStatutoryLeaveTemplateView[]>();
    for (const c of visible) {
      const list = map.get(c.countryId) ?? [];
      list.push(c);
      map.set(c.countryId, list);
    }
    return heldCountries.filter(hc => map.has(hc.id)).map(hc => ({ country: hc, rows: map.get(hc.id)! }));
  }, [visible, heldCountries]);

  const replaceCopy = (next: CompanyStatutoryLeaveTemplateView) =>
    setCopies(prev => prev.map(c => (c.id === next.id ? next : c)));

  const run = async (
    key: string,
    call: () => Promise<{ errors: unknown[]; payload?: unknown }>,
    success: string,
    after: (payload: unknown) => void,
  ): Promise<boolean> => {
    setBusy(key);
    try {
      const res = await call();
      if (res.errors.length > 0) {
        showToast(getApiErrorMessage(res, t, t('common:Error')), 'error');
        return false;
      }
      after(res.payload);
      showToast(success, 'success');
      return true;
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('common:Error')), 'error');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const save = (copy: CompanyStatutoryLeaveTemplateView) => async (input: CompanyStatutoryLeaveTemplateInput) => {
    const ok = await run(`save-${copy.id}`, () => LeavesApi.updateStatutoryTemplate(copy.id, input),
      t('leaves:statutoryTemplates.saved'), p => replaceCopy(p as CompanyStatutoryLeaveTemplateView));
    if (ok) setEditingId(null);
    return ok;
  };

  const archive = (copy: CompanyStatutoryLeaveTemplateView) => run(`archive-${copy.id}`,
    () => LeavesApi.archiveStatutoryTemplate(copy.id),
    t('leaves:statutoryTemplates.archivedToast', { name: copy.leaveTypeName }),
    () => replaceCopy({ ...copy, isArchived: true }));

  const restore = (copy: CompanyStatutoryLeaveTemplateView) => run(`restore-${copy.id}`,
    () => LeavesApi.restoreStatutoryTemplate(copy.id),
    t('leaves:statutoryTemplates.restoredToast', { name: copy.leaveTypeName }),
    p => replaceCopy((p as CompanyStatutoryLeaveTemplateView) ?? { ...copy, isArchived: false }));

  const reset = (copy: CompanyStatutoryLeaveTemplateView) => run(`reset-${copy.id}`,
    () => LeavesApi.resetStatutoryTemplateToPlatform(copy.id),
    t('leaves:statutoryTemplates.resetToast', { name: copy.leaveTypeName }),
    p => replaceCopy(p as CompanyStatutoryLeaveTemplateView));

  const countryOptions = [
    { value: ALL, label: t('leaves:statutoryTemplates.allCountries') },
    ...heldCountries.map(c => ({ value: String(c.id), label: c.iso ? `${c.name} (${c.iso})` : c.name })),
  ];

  return (
    <PageShell
      title={t('leaves:statutoryTemplates.title')}
      description={t('leaves:statutoryTemplates.companyDescription')}
      icon={Scale}
      accentColor="emerald"
      maxWidth="5xl"
    >
      <div className="flex flex-col gap-6" data-testid="statutory-templates-page">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <p className="text-sm text-muted-foreground" data-testid="statutory-company-country">
            {companyCountry
              ? <>{t('leaves:statutoryTemplates.companyCountry')} <strong className="font-semibold text-foreground">{companyCountry.name}</strong></>
              : t('leaves:statutoryTemplates.companyCountryUnset')}
          </p>
          {!loading && !loadError && copies.length > 0 && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              {showCountryFilter && (
                <div className="w-full sm:w-64">
                  <Select
                    id="statutory-country-filter"
                    label={t('leaves:statutoryTemplates.country')}
                    options={countryOptions}
                    value={effectiveCountry}
                    onChange={(v: string) => { setCountryFilter(v || ALL); setEditingId(null); }}
                    data-testid="country-filter"
                  />
                </div>
              )}
              <div
                role="group"
                aria-label={t('leaves:statutoryTemplates.viewLabel')}
                className="inline-flex w-full rounded-md border border-border bg-muted p-0.5 sm:w-auto"
              >
                {(['active', 'archived'] as View[]).map(v => (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={view === v}
                    onClick={() => { setView(v); setEditingId(null); }}
                    className={`flex-1 rounded px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 sm:flex-none ${
                      view === v ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid={`statutory-view-${v}`}
                  >
                    {v === 'active' ? t('leaves:statutoryTemplates.viewActive') : t('leaves:statutoryTemplates.viewArchived')}
                    <span className="ms-1.5 tabular-nums text-muted-foreground">{v === 'active' ? activeCount : archivedCount}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5" data-testid="statutory-loading">
            {[0, 1, 2].map(i => (
              <div key={i} className="flex flex-col gap-2 py-2">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-full max-w-md" />
              </div>
            ))}
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-5" role="alert">
            <p className="text-sm text-destructive">{loadError}</p>
            <Button variant="outline" size="sm" onClick={load}>{t('common:Retry')}</Button>
          </div>
        ) : copies.length === 0 ? (
          <EmptyCatalogue countryName={companyCountry?.name ?? null} />
        ) : groups.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-5 py-10 text-center text-sm text-muted-foreground" data-testid="no-templates">
            {view === 'archived' ? t('leaves:statutoryTemplates.noArchived') : t('leaves:statutoryTemplates.allArchived')}
          </p>
        ) : (
          groups.map(({ country, rows }) => (
            <section key={country.id} aria-labelledby={`statutory-country-${country.id}`} className="flex flex-col gap-2">
              {(showCountryFilter || (companyCountry != null && companyCountry.id !== country.id)) && (
                <h2 id={`statutory-country-${country.id}`} className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  {country.name}
                  {country.iso && <span className="text-xs font-normal text-muted-foreground">{country.iso}</span>}
                  {companyCountry != null && companyCountry.id !== country.id && (
                    <span className="text-xs font-normal text-muted-foreground">
                      {t('leaves:statutoryTemplates.previousCountryNote')}
                    </span>
                  )}
                </h2>
              )}
              <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card" data-testid={`statutory-country-list-${country.id}`}>
                {rows.map(copy => (
                  <CopyRow
                    key={copy.id}
                    copy={copy}
                    leaveTypes={leaveTypes}
                    editing={editingId === copy.id}
                    busy={busy}
                    onEdit={() => setEditingId(copy.id)}
                    onCancelEdit={() => setEditingId(null)}
                    onSave={save(copy)}
                    onArchive={() => { archive(copy); }}
                    onRestore={() => { restore(copy); }}
                    onReset={() => reset(copy)}
                  />
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageShell>
  );
};

/** Section 5.1: a company whose country has no catalogue rows, or no country, holds no copies. Not an error. */
function EmptyCatalogue({ countryName }: { countryName: string | null }) {
  const { t } = useTranslation(['leaves', 'common']);
  return (
    <div className="flex flex-col items-start gap-2 rounded-lg border border-dashed border-border px-5 py-8" data-testid="no-templates">
      <Scale className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
      {countryName ? (
        <>
          <p className="text-sm font-medium text-foreground">{t('leaves:statutoryTemplates.emptyCountry', { country: countryName })}</p>
          <p className="max-w-prose text-sm text-muted-foreground">{t('leaves:statutoryTemplates.emptyCountryHint')}</p>
        </>
      ) : (
        <>
          <p className="text-sm font-medium text-foreground">{t('leaves:statutoryTemplates.emptyNoCountry')}</p>
          <Link to="/settings/general-details" className="text-sm font-medium text-primary hover:underline">
            {t('leaves:statutoryTemplates.openGeneralDetails')}
          </Link>
        </>
      )}
    </div>
  );
}

export default StatutoryLeaveTemplates;
